import { z } from 'zod';
import type { LocationProviderType } from '../enums/location.js';
import { KolSearchResultModel } from './kol.js';
import type { GeoPoint } from './location.js';
import type { AWSStep } from './smart-routing.aws.js';

export type IdentifiedPoint = {
  id: string;
} & GeoPoint;

type AWSLegMeta = {
  steps: AWSStep[];
};

type LegMetaMap = {
  [LocationProviderType.AWS]: AWSLegMeta;
  [LocationProviderType.Azure]: { points: GeoPoint[] };
};

type LegMeta<T extends LocationProviderType> = LegMetaMap[T];

export type RouteLeg<T extends LocationProviderType> = {
  start: IdentifiedPoint;
  end: IdentifiedPoint;
  distanceMiles: number;
  durationSeconds: number;
  meta: LegMeta<T>;
};

const ICoordsSchema = z.object({
  latitude: z.number(),
  longitude: z.number(),
});

const StartingLocationSchema = z.union([
  z
    .object({
      type: z.literal('search'),
      id: z.string(),
      name: z.string(),
    })
    .extend(ICoordsSchema.shape),
  z
    .object({
      type: z.literal('browser'),
    })
    .extend(ICoordsSchema.shape),
  z
    .object({
      type: z.literal('hcp'),
      id: z.number(),
      name: z.string(),
    })
    .extend(ICoordsSchema.shape),
]);

const LocationEntitySchema = z.object({
  id: z.number(),
  name: z.string(),
  type: z.literal('hcp'),
});

const LocationInfoSchema = z.object({
  latitude: z.number(),
  longitude: z.number(),
  address: z.string().nullable(),
  entity: LocationEntitySchema.nullable(),
});

const LocationRoutingLegSchema = z.object({
  start: LocationInfoSchema,
  end: LocationInfoSchema,
  distanceMiles: z.number(),
  durationSeconds: z.number(),
  meta: z.any(), // This could be more specific based on LocationProviderType
});

export const SmartRoutingSchema = z.object({
  status: z
    .enum(['select-location', 'submit-location', 'select-hcps', 'submit-route', 'route-generated'])
    .nullable(),
  startingLocation: StartingLocationSchema.nullable(),
  selectedHcps: z.array(z.number()).nullable(),
  routingResult: z
    .object({
      legs: z.array(LocationRoutingLegSchema),
      provider: z.string(), // Adjust this based on LocationProviderType
    })
    .nullable(),
  hcpId: z.string().nullable().optional(),
  hideLocationForm: z.boolean().optional(),
  kolProfile: KolSearchResultModel.nullable().optional(),
});
